import { useQuery } from "@tanstack/react-query";
import { useAuthenticatedFetch } from "@/hooks/use-authenticated-fetch";
import { recordKeys } from "@/lib/query-keys";
import { normalizeRecordImageShape } from "@/offline/record-shape";
import type { ProjectRecord } from "@/types/api";

/**
 * A single record, read from the v2.0 endpoint so `images[]` is the full photo
 * set rather than just the legacy cover.
 *
 * Used by the issue detail screen for its evidence strip: an issue carries only
 * its record's id and cover thumbnail, so the screen that needs every photo
 * reads the record itself — one request on a detail page, rather than one per
 * row on a list.
 */
export function useProjectRecord(projectId: string, recordId: string | undefined) {
  const fetchWithAuth = useAuthenticatedFetch();

  return useQuery({
    queryKey: recordKeys.detail(projectId, recordId ?? ""),
    // The v2.0 `images[]` wire carries only `*_url` derivatives; normalize to
    // the canonical `{ image, thumbnail }` shape every photo reader expects, as
    // the records list does. See record-shape.
    queryFn: async () =>
      normalizeRecordImageShape(
        await fetchWithAuth<ProjectRecord>(
          `/projects/2.0/projects/${projectId}/records/${recordId}/`,
        ),
      ),
    enabled: Boolean(projectId && recordId),
  });
}
